import { useEffect, useState } from "react";
import { Link, useParams, Navigate } from "react-router-dom";
import { ArrowRight, Phone } from "lucide-react";
import { Button } from "../components/ui/button";
import Seo from "../components/Seo";
import PageHero from "../components/PageHero";
import PageSections from "../components/PageSections";
import { fetchPage } from "../lib/api";
import { PACKAGES, getPackage } from "../lib/packages";
import IntensiveCourses from "./IntensiveCourses";

export default function PackageDetail() {
  const { slug } = useParams();
  const pkg = getPackage(slug);
  const [page, setPage] = useState(null);
  const [pageLoaded, setPageLoaded] = useState(false);
  useEffect(() => {
    if (pkg) {
      setPageLoaded(false);
      fetchPage(`packages-${slug}`)
        .then(setPage)
        .catch(() => {})
        .finally(() => setPageLoaded(true));
    }
  }, [slug, pkg]);

  // The bespoke slug is now an Intensive Courses page with its own layout
  // (SVG map, course cards, enquire modal). Delegate AFTER hooks have been
  // declared so we don't violate the rules-of-hooks on route switches.
  if (slug === "bespoke") return <IntensiveCourses />;
  if (!pkg) return <Navigate to="/packages" replace />;

  const bookHref = pkg.enquireOnly ? "/contact" : `/book?product=${pkg.product}`;
  const sections = page?.sections || [];
  const intro = sections.find(s => s.type === "intro");
  // Detail body sections: everything EXCEPT the intro (intro is rendered inline with the booking CTA).
  const bodySections = sections.filter(s => s !== intro);

  return (
    <>
      <Seo
        title={page?.meta_title || `${pkg.title} — Lime Driving School`}
        description={page?.meta_description || pkg.blurb}
        jsonLd={page?.schema_json}
      />
      <PageHero
        eyebrow="Our courses"
        title={page?.title || pkg.title}
        subtitle={pkg.headline}
        showCta={false}
      />

      {/* Intro + booking CTA */}
      <section className="py-12 md:py-16 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-testid={`package-detail-${pkg.slug}`}>
        <div className="grid md:grid-cols-12 gap-10 items-start">
          <div className="md:col-span-5">
            {pageLoaded ? (
              <img src={intro?.image || pkg.image} alt={intro?.image_alt || pkg.title}
                   className="w-full rounded-3xl border border-black/5 shadow-sm aspect-[4/5] object-cover animate-[fadein_400ms_ease-out]" />
            ) : (
              <div className="w-full rounded-3xl border border-black/5 shadow-sm aspect-[4/5] bg-[#F3F4F6]" />
            )}
          </div>
          <div className="md:col-span-7 space-y-7">
            <div>
              {intro?.eyebrow && (
                <span className="text-xs uppercase tracking-[0.2em] font-semibold text-[#4B5563]">{intro.eyebrow}</span>
              )}
              <h2 className="mt-2 text-2xl sm:text-3xl tracking-tight font-medium" style={{fontFamily:"Outfit, sans-serif"}}>
                {intro?.title || pkg.price}
              </h2>
              {intro?.body && (
                <div className="mt-4 prose prose-neutral max-w-none" dangerouslySetInnerHTML={{ __html: intro.body }} />
              )}
            </div>
            <div className="flex flex-wrap items-center gap-4">
              <Button asChild className="bg-[#37B160] hover:bg-[#2D9450] text-white h-12 px-6 rounded-2xl" data-testid={`book-now-${pkg.slug}`}>
                <Link to={bookHref}>
                  {pkg.enquireOnly ? "Enquire now" : "Book now"} <ArrowRight className="h-4 w-4 ml-1.5" />
                </Link>
              </Button>
              <a href="tel:07472887780" className="inline-flex items-center gap-2 text-[#111827] hover:text-[#37B160] font-medium">
                <Phone className="h-4 w-4" /> 07472 887780
              </a>
              <Link to="/refund-policy" className="text-sm text-[#4B5563] hover:text-[#111827] underline underline-offset-4">
                Refund policy
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Remaining CMS sections (icon features, checklist, etc) */}
      <PageSections sections={bodySections} />

      {/* Cross-sell */}
      <section className="pb-16 md:pb-24 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="pt-12 border-t border-black/5">
          <h3 className="text-lg font-medium" style={{fontFamily:"Outfit, sans-serif"}}>Other packages</h3>
          <div className="mt-4 grid sm:grid-cols-3 gap-4">
            {PACKAGES.filter(p => p.slug !== pkg.slug).map(other => (
              <Link key={other.slug} to={`/packages/${other.slug}`}
                    className="bg-white border border-black/5 rounded-2xl p-5 hover:shadow-md transition-all"
                    data-testid={`cross-sell-${other.slug}`}>
                <div className="text-xs uppercase tracking-[0.2em] font-semibold text-[#37B160]">{other.headline}</div>
                <div className="mt-1.5 font-medium" style={{fontFamily:"Outfit, sans-serif"}}>{other.title}</div>
                <div className="mt-1 text-sm text-[#4B5563]">{other.price}</div>
              </Link>
            ))}
          </div>
        </div>
      </section>
    </>
  );
}
